import { ProductPageTemplate } from "@/components/site/ProductPageTemplate";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    title: "Tires & Wheels",
    metaTitle: 'Trailer Tires & Wheels in Elkhart, IN - 8" to 17.5"',
    metaDescription: 'Trailer tires and wheels in Elkhart, IN - 8" through 17.5" sizes. Steel, aluminum, chrome, and galvanized finishes. Mount-and-balance available at the shop.',
    hero: {
      eyebrow: "Tires · Wheels · Assemblies",
      heading: "From utility to commercial duty.",
      lede: "Trailer tires and wheels in every common size and style - including 17.5-inch commercial wheels for heavy haulers. Mounted, balanced, and ready to roll.",
    },
    intro: "Trailer tires aren't passenger tires. We carry trailer-rated tires (ST and commercial radials) in the load ranges and ply ratings that actually match your application - paired with the right wheel for the right look and the right longevity.",
    features: [
      "ST trailer tires - radial and bias",
      '8" through 17.5" wheel diameters',
      "Steel wheels - black, white, and primer",
      "Aluminum wheels - polished and machined",
      "Chrome and galvanized wheels",
      "Spoke, modular, and conventional patterns",
      "Mounted-and-balanced tire/wheel assemblies",
      "Spare tire mounts and accessories",
    ],
    capacityTable: [
      { label: "Wheel sizes", value: '8" · 12" · 13" · 14" · 15" · 16" · 17.5"' },
      { label: "Bolt patterns", value: '5/4.5" · 6/5.5" · 8/6.5" + more' },
      { label: "Tire types", value: "ST radial · ST bias · Commercial radial" },
      { label: "Finishes", value: "Steel · Aluminum · Chrome · Galv." },
    ],
    variants: [
      { name: "ST205/75R15 on 15\" steel", desc: "The most common upgrade size for light tandem trailers. Load Range D or E available." },
      { name: "ST225/75R15 on 15\" aluminum", desc: "A clean look upgrade for boat and recreational trailers." },
      { name: "16\" trailer assembly", desc: "For 7k–10k lb axles on heavier landscape and equipment trailers." },
      { name: "Galvanized wheel", desc: "The right call for boat trailers, where saltwater and steel don't mix." },
    ],
    related: [
      { title: "Hubs, Drums & Brakes", desc: "Match bolt patterns and hub styles." },
      { title: "Suspension Parts", desc: "Springs, hangers, and equalizers." },
      { title: "Trailer Repair", desc: "Mount, balance, and bearing service." },
    ],
  },
  es: {
    title: "Llantas y Ruedas",
    metaTitle: 'Llantas y ruedas para remolque en Elkhart, IN - 8" a 17.5"',
    metaDescription: 'Llantas y ruedas para remolque en Elkhart, IN - medidas de 8" a 17.5". Acabados en acero, aluminio, cromo y galvanizado. Servicio de montaje y balanceo disponible en el taller.',
    hero: {
      eyebrow: "Llantas · Ruedas · Conjuntos",
      heading: "Desde uso utilitario hasta servicio comercial.",
      lede: "Llantas y ruedas para remolque en todas las medidas y estilos comunes, incluyendo ruedas comerciales de 17.5 pulgadas para cargas pesadas. Montadas, balanceadas y listas para rodar.",
    },
    intro: "Las llantas para remolque no son llantas para pasajeros. Contamos con llantas clasificadas para remolque (radiales ST y comerciales) en los rangos de carga y clasificaciones de capas que realmente se ajustan a su aplicación, combinadas con la rueda correcta para el aspecto y la longevidad adecuados.",
    features: [
      "Llantas para remolque ST - radiales y convencionales",
      'Diámetros de rueda de 8" a 17.5"',
      "Ruedas de acero - negro, blanco y primer",
      "Ruedas de aluminio - pulido y maquinado",
      "Ruedas cromadas y galvanizadas",
      "Patrones de radios, modulares y convencionales",
      "Conjuntos de llanta y rueda montados y balanceados",
      "Soportes para llanta de refacción y accesorios",
    ],
    capacityTable: [
      { label: "Medidas de rueda", value: '8" · 12" · 13" · 14" · 15" · 16" · 17.5"' },
      { label: "Patrones de birlos", value: '5/4.5" · 6/5.5" · 8/6.5" + más' },
      { label: "Tipos de llanta", value: "ST radial · ST convencional · Radial comercial" },
      { label: "Acabados", value: "Acero · Aluminio · Cromo · Galv." },
    ],
    variants: [
      { name: "ST205/75R15 en acero de 15\"", desc: "La medida de mejora más común para remolques ligeros en tándem. Rango de carga D o E disponible." },
      { name: "ST225/75R15 en aluminio de 15\"", desc: "Una mejora de aspecto limpio para remolques recreativos y de lanchas." },
      { name: "Conjunto de remolque de 16\"", desc: "Para ejes de 7k–10k libras en remolques de equipo y jardinería más pesados." },
      { name: "Rueda galvanizada", desc: "La elección correcta para remolques de lanchas, donde el agua salada y el acero no se llevan bien." },
    ],
    related: [
      { title: "Mazas, Tambores y Frenos", desc: "Combine patrones de birlos y estilos de maza." },
      { title: "Partes de suspensión", desc: "Ballestas, soportes y ecualizadores." },
      { title: "Reparación de remolques", desc: "Servicio de montaje, balanceo y rodamientos." },
    ],
  },
};

export default function TiresWheels() {
  const t = useCopy(COPY);
  return (
    <ProductPageTemplate
      slug="tires-wheels"
      title={t.title}
      metaTitle={t.metaTitle}
      metaDescription={t.metaDescription}
      hero={{
        eyebrow: t.hero.eyebrow,
        heading: t.hero.heading,
        lede: t.hero.lede,
      }}
      intro={t.intro}
      features={t.features}
      capacityTable={t.capacityTable}
      variants={t.variants}
      related={[
        { title: t.related[0].title, path: "/products/hubs/", desc: t.related[0].desc },
        { title: t.related[1].title, path: "/products/suspension-parts/", desc: t.related[1].desc },
        { title: t.related[2].title, path: "/services/axle-repair/", desc: t.related[2].desc },
      ]}
    />
  );
}
